import { TrashIcon } from "@heroicons/react/20/solid";
import { ReactNode } from "react";
import { Badge } from "@/catalyst/badge";
import Button from "@/common/Button";
import { ChoiceChips } from "@/common/ChoiceChips";
import {
    ComboBoxMultiple,
    ComboBoxMultipleOption,
} from "@/common/ComboBoxMultiple";
import { FormRow } from "@/common/FormLayout";
import { Segmented, SegmentedTone } from "@/common/Segmented";
import Tooltip from "@/common/Tooltip";
import {
    Fact,
    FactField,
    optionsFor,
    Weight,
    WEIGHT_LABELS,
    WEIGHT_ORDER,
    WEIGHTS,
    weightOf,
} from "@/features/IcpFit/icp.ts";
import { IcpVocabularyResponse } from "@/types/generated/management-api-types";

/** The chosen values as the picker's options, so a chip keeps its name while the vocabulary loads. */
function selectedOptions(
    values: string[],
    options: ComboBoxMultipleOption[],
): ComboBoxMultipleOption[] {
    const byId = new Map(options.map((o) => [o.id, o]));
    return values.map(
        (value) => byId.get(value) ?? { id: value, label: value },
    );
}

/**
 * Facts with a handful of values show them all as chips. A dropdown would hide
 * three or five options behind a click and a search box.
 */
const CHIP_FIELDS: FactField[] = ["businessModel"];

/**
 * Who a company sells to is stored as one of three answers: businesses,
 * consumers, or a mix of both. The editor offers the first two only. A third
 * chip for the mix overlapped the two beside it, and nobody could say what
 * ticking it alone meant.
 */
const MIXED = "both";

/** The chips that are on for a stored rule. */
function audiencesOf(values: string[]): string[] {
    const audiences = values.filter((value) => value !== MIXED);
    // A rule on the mix alone is both audiences.
    return audiences.length === 0 && values.includes(MIXED)
        ? ["b2b", "b2c"]
        : audiences;
}

/**
 * The rule for the chips that are on. As a fit, an audience includes the
 * companies that sell to it among others, so the mix comes along. Ruling one
 * out leaves the mix alone, since a mixed company still sells to the other;
 * only ruling out both takes it too.
 */
function audienceValues(audiences: string[], ruleOut: boolean): string[] {
    if (audiences.length === 0) return [];
    return !ruleOut || audiences.length === 2
        ? [...audiences, MIXED]
        : audiences;
}

/** The values of a fact: chips for a short list, a searchable picker for a long one. */
function ValuePicker({
    fact,
    values,
    vocabulary,
    placeholder,
    disabled,
    ruleOut = false,
    onChange,
}: {
    fact: Fact;
    values: string[];
    vocabulary: IcpVocabularyResponse | undefined;
    placeholder: string;
    disabled: boolean;
    /** The values rule a company out, instead of making it a fit. */
    ruleOut?: boolean;
    onChange: (values: string[]) => void;
}) {
    const options = optionsFor(fact.field, vocabulary);
    if (fact.field === "b2bB2c") {
        return (
            <ChoiceChips
                label={fact.label}
                options={options.filter((option) => option.id !== MIXED)}
                value={audiencesOf(values)}
                onChange={(audiences) =>
                    onChange(audienceValues(audiences, ruleOut))
                }
                disabled={disabled}
            />
        );
    }
    if (CHIP_FIELDS.includes(fact.field)) {
        return (
            <ChoiceChips
                label={fact.label}
                // Until the list has loaded, the chosen ones at least.
                options={
                    options.length > 0
                        ? options
                        : selectedOptions(values, options)
                }
                value={values}
                onChange={onChange}
                disabled={disabled}
            />
        );
    }
    return (
        <ComboBoxMultiple
            options={options}
            value={selectedOptions(values, options)}
            onChange={(selected) => onChange(selected.map((o) => o.id))}
            placeholder={placeholder}
            disabled={disabled}
            immediate
            accent
        />
    );
}

/** The more a rule counts, the more of the accent its weight gets. */
const WEIGHT_TONES: Record<Weight, SegmentedTone> = {
    low: "neutral",
    medium: "soft",
    high: "solid",
};

/** How much a rule counts, as three steps. */
export function WeightPicker({
    label,
    points,
    onChange,
    disabled,
}: {
    label: string;
    points: number;
    onChange: (points: number) => void;
    disabled: boolean;
}) {
    return (
        <Segmented<Weight>
            size={"sm"}
            label={label}
            options={WEIGHT_ORDER}
            value={weightOf(points)}
            onChange={(weight) => onChange(WEIGHTS[weight])}
            renderOption={(weight) => WEIGHT_LABELS[weight]}
            tone={(weight) => WEIGHT_TONES[weight]}
            disabled={disabled}
        />
    );
}

/**
 * The column a weight sits in. A row with nothing to weigh keeps the room, so
 * its field is as wide as the ones around it: an unseen picker, which is the
 * column's exact width where a fixed one left a gap beside the picker.
 */
export function WeightColumn({ children }: { children?: ReactNode }) {
    return (
        children || (
            <div className={"invisible"} aria-hidden={"true"} inert>
                <WeightPicker
                    label={""}
                    points={WEIGHTS.medium}
                    onChange={() => undefined}
                    disabled
                />
            </div>
        )
    );
}

/** What that column is called, on the first row of it. */
export const WEIGHT_CAPTION = "How much it counts";

/**
 * Beside a fact's name when the data behind it needs Lens this organization
 * does not have.
 */
function NeedsLensBadge() {
    return (
        <Tooltip
            className={"flex"}
            content={
                "Needs Lens. Without it this fact is unknown for every company and its points count towards Unknown."
            }
        >
            <Badge color={"amber"}>Needs Lens</Badge>
        </Tooltip>
    );
}

/**
 * One fact of the ideal customer: the values that fit, and how much the
 * fact counts. No values means the fact plays no part, so the weight only
 * appears once there is something to weigh.
 */
export function FactRow({
    fact,
    values,
    points,
    vocabulary,
    hasLens,
    disabled,
    lockedReason,
    captioned,
    onChange,
}: {
    fact: Fact;
    values: string[];
    points: number;
    vocabulary: IcpVocabularyResponse | undefined;
    hasLens: boolean;
    disabled: boolean;
    lockedReason: string | null;
    /** The first fact names the weight column. */
    captioned: boolean;
    onChange: (values: string[], points: number) => void;
}) {
    return (
        <FormRow
            label={fact.label}
            icon={fact.icon}
            badge={fact.needsLens && !hasLens && <NeedsLensBadge />}
            description={fact.hint}
            lockedReason={lockedReason}
            asideCaption={captioned ? WEIGHT_CAPTION : undefined}
            asideColumn
            aside={
                <WeightColumn>
                    {values.length > 0 && (
                        <WeightPicker
                            label={`How much ${fact.label.toLowerCase()} counts`}
                            points={points}
                            onChange={(next) => onChange(values, next)}
                            disabled={disabled}
                        />
                    )}
                </WeightColumn>
            }
        >
            <ValuePicker
                fact={fact}
                values={values}
                vocabulary={vocabulary}
                placeholder={fact.any}
                disabled={disabled}
                onChange={(next) => onChange(next, points)}
            />
        </FormRow>
    );
}

/** A deal-breaker: companies with one of these values are a Poor fit whatever else is true. */
export function RuleOutRow({
    fact,
    values,
    vocabulary,
    hasLens,
    disabled,
    lockedReason,
    onChange,
    onRemove,
}: {
    fact: Fact;
    values: string[];
    vocabulary: IcpVocabularyResponse | undefined;
    hasLens: boolean;
    disabled: boolean;
    lockedReason: string | null;
    onChange: (values: string[]) => void;
    onRemove: () => void;
}) {
    return (
        <FormRow
            label={fact.label}
            icon={fact.icon}
            badge={fact.needsLens && !hasLens && <NeedsLensBadge />}
            description={fact.hint}
            lockedReason={lockedReason}
            aside={
                !disabled && (
                    <Button
                        plain
                        icon={TrashIcon}
                        onClick={onRemove}
                        aria-label={`Stop ruling out on ${fact.label.toLowerCase()}`}
                        tooltip={"Remove"}
                    />
                )
            }
        >
            <ValuePicker
                fact={fact}
                values={values}
                vocabulary={vocabulary}
                placeholder={"Choose…"}
                disabled={disabled}
                ruleOut
                onChange={onChange}
            />
        </FormRow>
    );
}
